import type { OnboardingStepDTO } from "@zenith/services/user";
import {
  CLIENT_ERROR_BAD_REQUEST,
  WRONG_AUTHENTICATION_CODE_ERROR_CODE,
} from "@zenith/data/constants";
import { OpenAPI } from "@zenith/services/user/core/OpenAPI";
import {
  BUSINESS_VERIFICATION,
  BUSINESS_VERIFICATION_ACCOUNT,
  BUSINESS_VERIFICATION_BUSINESS_INFO,
  BUSINESS_VERIFICATION_DOCUMENTS,
  BUSINESS_VERIFICATION_PEOPLE,
  BUSINESS_VERIFICATION_PERSONAL_DETAILS,
  BUSINESS_VERIFICATION_PHOTO_VERIFICATION,
  BUSINESS_VERIFICATION_REVIEW,
} from "@zenith/routes/paths";

export const STEP_LABEL_MAP: Record<string, string> = {
  PENDING_PERSONAL_DETAILS: "Personal Details",
  PENDING_SELFIE_VERIFICATION: "Selfie Verification",
  PENDING_BUSINESS_INFORMATION: "Business Information",
  PENDING_PEOPLE: "People",
  PENDING_SET_ACCOUNT: "Account",
  PENDING_PAYOUT_ACCOUNTS: "Account",
  PENDING_REQUIRED_DOCUMENTS: "Documents",
};

export const STEP_ROUTE_MAP: Record<string, string> = {
  PENDING_PERSONAL_DETAILS: BUSINESS_VERIFICATION_PERSONAL_DETAILS,
  PENDING_SELFIE_VERIFICATION: BUSINESS_VERIFICATION_PHOTO_VERIFICATION,
  PENDING_BUSINESS_INFORMATION: BUSINESS_VERIFICATION_BUSINESS_INFO,
  PENDING_REQUIRED_DOCUMENTS: BUSINESS_VERIFICATION_DOCUMENTS,
  PENDING_PEOPLE: BUSINESS_VERIFICATION_PEOPLE,
  PENDING_SET_ACCOUNT: BUSINESS_VERIFICATION_ACCOUNT,
  PENDING_PAYOUT_ACCOUNTS: BUSINESS_VERIFICATION_ACCOUNT,
  // Every data-entry step is done — nothing left but Review.
  PENDING_SUBMIT: BUSINESS_VERIFICATION_REVIEW,
};

export function getStepRoute(nextStep: string | undefined | null): string {
  if (!nextStep) return BUSINESS_VERIFICATION;
  return STEP_ROUTE_MAP[nextStep] ?? BUSINESS_VERIFICATION;
}

export function isMultiSignatoryFlow(
  steps: OnboardingStepDTO[] | undefined
): boolean {
  return !!steps?.some((s) => s.stepName === "PENDING_PEOPLE");
}

export const HIDDEN_STEPS = new Set([
  "PENDING_REGISTRATION_TYPE",
  "PENDING_SUBMIT",
  "PENDING_SET_ACCOUNT",
  "PENDING_SELFIE_VERIFICATION",
  "PENDING_VERIFY_OTP",
  "PENDING_CREATE_ACCOUNT",
]);

export function withPersonalDetailsSelfieGate(
  steps: OnboardingStepDTO[]
): OnboardingStepDTO[] {
  const personalDetails = steps.find(
    (s) => s.stepName === "PENDING_PERSONAL_DETAILS"
  );
  const selfie = steps.find(
    (s) => s.stepName === "PENDING_SELFIE_VERIFICATION"
  );
  if (!personalDetails || !selfie || selfie.status === "COMPLETED") {
    return steps;
  }
  return steps.map((s) =>
    s.stepName === "PENDING_PERSONAL_DETAILS"
      ? { ...s, status: selfie.status }
      : s
  );
}

export type BusinessRouteState = {
  registrationSteps?: OnboardingStepDTO[];
  registrationType?: string;
  submitted?: boolean;
};

const DATA_ENTRY_STATUSES = new Set([
  "PENDING_REGISTRATION_TYPE",
  "PENDING_PERSONAL_DETAILS",
  "PENDING_SELFIE_VERIFICATION",
  "PENDING_BUSINESS_INFORMATION",
  "PENDING_REQUIRED_DOCUMENTS",
  "PENDING_PAYOUT_ACCOUNTS",
  "PENDING_SET_ACCOUNT",
  "PENDING_PEOPLE",
  "PENDING_SUBMIT",
]);

export function isPostSubmissionStatus(
  currentStatus: string | undefined | null
): boolean {
  return !!currentStatus && !DATA_ENTRY_STATUSES.has(currentStatus);
}

export type ReviewState = "APPROVED" | "REJECTED" | "UNDER_REVIEW" | "UPDATED";

// Combines a section's one or more reviewStatus values (e.g. a document per
// array item vs. a single business-profile field) into one pill state: any
// rejection wins outright, then any change made since the rejection
// ("UPDATED"); everything else only reads as approved once every item
// actually is — otherwise it's still under review.
//
// treatMissingAsUpdated: once compliance has sent something back, an item
// with no reviewStatus was added after the rejection (a replaced document,
// a new account), so it counts as an update too.
export function combineReviewStatuses(
  statuses: Array<string | undefined | null>,
  treatMissingAsUpdated = false
): ReviewState {
  if (statuses.some((s) => s === "REJECTED")) return "REJECTED";
  if (
    statuses.some((s) => s === "UPDATED" || (treatMissingAsUpdated && !s))
  ) {
    return "UPDATED";
  }
  if (statuses.length > 0 && statuses.every((s) => s === "APPROVED")) {
    return "APPROVED";
  }
  return "UNDER_REVIEW";
}

export type RejectedField = { field?: string; value?: string; reason?: string };

export type ReviewFeedback = {
  isRejected: boolean;
  // A plain-text reviewComment (documents, accounts)
  message?: string;
  // A structured reviewComment (business sections, stakeholders)
  rejectedFields: RejectedField[];
};

type Reviewable =
  | { reviewStatus?: string; reviewComment?: string }
  | null
  | undefined;

// reviewComment is either plain text ("Expired document") or a JSON string
// like {"section":"Business Profile","rejectedFields":[{"field":"Staff
// Strength","value":"11 - 20","reason":"Incomplete information"}]}.
export function parseReviewFeedback(item: Reviewable): ReviewFeedback {
  const isRejected = item?.reviewStatus === "REJECTED";
  const comment = item?.reviewComment?.trim();
  if (!comment) return { isRejected, rejectedFields: [] };
  try {
    const parsed = JSON.parse(comment);
    if (parsed && Array.isArray(parsed.rejectedFields)) {
      return {
        isRejected,
        rejectedFields: parsed.rejectedFields.filter(
          (f: unknown): f is RejectedField => !!f && typeof f === "object"
        ),
      };
    }
  } catch {
    // Not JSON — use it as a plain-text comment
  }
  return { isRejected, message: comment, rejectedFields: [] };
}

export function formatRejectedField(field: RejectedField): string {
  const reason = field.reason || "Rejected";
  return field.field ? `${field.field}: ${reason}` : reason;
}

// Every reason a rejected item carries, ready to list in its tooltip.
export function getRejectionReasons(feedback: ReviewFeedback): string[] {
  if (!feedback.isRejected) return [];
  if (feedback.rejectedFields.length > 0) {
    return feedback.rejectedFields.map(formatRejectedField);
  }
  return feedback.message ? [feedback.message] : [];
}

const normalizeFieldName = (name?: string) =>
  (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Places a section's rejected fields on the page's own fields by any of their
// known names. Whatever can't be placed (or a plain-text comment) comes back
// in `unmatched` so the section itself can still show it.
export function matchRejectedFields<K extends string>(
  feedbacks: ReviewFeedback[],
  fieldNames: Record<K, string[]>
): { byField: Partial<Record<K, RejectedField>>; unmatched: string[] } {
  const byField: Partial<Record<K, RejectedField>> = {};
  const unmatched: string[] = [];
  const keys = Object.keys(fieldNames) as K[];

  feedbacks
    .filter((feedback) => feedback.isRejected)
    .forEach((feedback) => {
      if (feedback.message) unmatched.push(feedback.message);
      feedback.rejectedFields.forEach((field) => {
        const name = normalizeFieldName(field.field);
        const key = keys.find((k) =>
          fieldNames[k].some((alias) => normalizeFieldName(alias) === name)
        );
        if (key && !byField[key]) byField[key] = field;
        else unmatched.push(formatRejectedField(field));
      });
    });

  return { byField, unmatched };
}

export const SESSION_INVALID_INTERNAL_CODE = "ZENZ-CROSS-003";

export function isSessionInvalidError(
  source: { internalCode?: string } | null | undefined
): boolean {
  return source?.internalCode === SESSION_INVALID_INTERNAL_CODE;
}

// The verification token for an email/phone is missing or no longer valid,
// so the merchant has to verify that contact again before saving.
export function isMfaTokenError(
  source: { internalCode?: string } | null | undefined
): boolean {
  return source?.internalCode === WRONG_AUTHENTICATION_CODE_ERROR_CODE;
}

const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  [WRONG_AUTHENTICATION_CODE_ERROR_CODE]:
    "Your verification has expired. Please verify your contact details again to continue.",
};

// Validation errors arrive as "contactInformation.businessEmail: Business email
// must be a valid email address"; the request field path means nothing to the user.
const FIELD_PATH_PREFIX_REGEX = /(^|;\s*)[\w.[\]]+:\s*/g;

// Some API messages are technical (and can echo back user data), so known
// internal codes get a friendlier message before falling back to the API's own.
export function getFriendlyErrorMessage(
  source: { internalCode?: string; message?: string } | null | undefined,
  fallback: string
): string {
  const friendlyMessage = FRIENDLY_ERROR_MESSAGES[source?.internalCode ?? ""];
  if (friendlyMessage) return friendlyMessage;

  if (source?.internalCode === CLIENT_ERROR_BAD_REQUEST && source.message) {
    return (
      source.message.replace(FIELD_PATH_PREFIX_REGEX, "$1").trim() || fallback
    );
  }

  return source?.message ?? fallback;
}

export function normalizeNigerianPhone(raw: string | undefined | null): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length < 10) return raw ?? "";
  return `+234${digits.slice(-10)}`;
}

export function capPhoneNumberInput(value: string): string {
  if (value.trim().startsWith("+234")) {
    const rest = value.slice(value.indexOf("+234") + 4).replace(/\D/g, "");
    return `+234${rest.slice(0, 10)}`;
  }
  return value.replace(/\D/g, "").slice(0, 11);
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve((reader.result as string).split(",")[1] ?? "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const ACCEPTED_FILE_MIME_TYPES: Record<"jpeg" | "png" | "pdf", string> = {
  jpeg: "image/jpeg",
  png: "image/png",
  pdf: "application/pdf",
};

export function getFileMimeExt(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "jpg") return ACCEPTED_FILE_MIME_TYPES.jpeg;
  if (ext === "jpeg" || ext === "png" || ext === "pdf") {
    return ACCEPTED_FILE_MIME_TYPES[ext];
  }
  return ACCEPTED_FILE_MIME_TYPES.jpeg;
}

export function getBareFileExtension(file: File): "jpeg" | "png" | "pdf" {
  const mimeType = file.type || getFileMimeExt(file);
  if (mimeType === "image/png") return "png";
  if (mimeType === "application/pdf") return "pdf";
  return "jpeg";
}

export function getFileExtension(file: File): string {
  const dot = file.name.lastIndexOf(".");
  return dot === -1 ? "" : file.name.slice(dot);
}

// Turns a base64 payload (as returned by e.g. getBoardResolutionTemplate)
// into a browser download. mimeExt may come back as a bare extension
// ("pdf") or a full MIME type ("application/pdf") — both are handled.
export function downloadBase64File(
  content: string,
  fileName: string,
  mimeExt?: string
): void {
  const knownMimeTypes: Record<string, string> = ACCEPTED_FILE_MIME_TYPES;
  const mimeType = mimeExt?.includes("/")
    ? mimeExt
    : knownMimeTypes[mimeExt?.toLowerCase() ?? ""] ??
      "application/octet-stream";

  const byteChars = atob(content);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

// downloadDocument streams the file inline (no auth required) rather than
// returning base64 content, so this is directly navigable — e.g. for the
// consent screen's board resolution Preview link. imageId is the
// document's storagePath, same convention as getOpenPaymentLinkLogoUrl.
export function getDocumentPreviewUrl(imageId: string): string {
  return `${
    OpenAPI.BASE
  }/api/onboarding/corporate/download/document?imageId=${encodeURIComponent(
    imageId
  )}`;
}

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "application/pdf": ".pdf",
  "image/png": ".png",
  "image/jpeg": ".jpg",
};

// Loads a stored onboarding document (e.g. a stakeholder's idCardPath) as a
// File, so it can sit in a drop zone and be sent again like a fresh upload.
export async function fetchStoredDocument(
  storagePath: string,
  fallbackName: string
): Promise<File> {
  const response = await fetch(getDocumentPreviewUrl(storagePath));
  if (!response.ok) {
    throw new Error(`Document download failed (${response.status})`);
  }
  const blob = await response.blob();
  const storedName = decodeURIComponent(
    storagePath.split("/").pop() ?? ""
  ).trim();
  const name = /\.[a-z0-9]+$/i.test(storedName)
    ? storedName
    : `${fallbackName}${EXTENSION_BY_MIME_TYPE[blob.type] ?? ""}`;
  return new File([blob], name, { type: blob.type });
}

const SYNTHETIC_DOCUMENTS_STEP: OnboardingStepDTO = {
  stepName: "PENDING_REQUIRED_DOCUMENTS",
  status: "PENDING",
  required: true,
};

export function withDocumentsStep(
  steps: OnboardingStepDTO[] | undefined
): OnboardingStepDTO[] {
  if (!steps || steps.length === 0) return steps ?? [];
  if (steps.some((s) => s.stepName === "PENDING_REQUIRED_DOCUMENTS")) {
    return steps;
  }

  const peopleIndex = steps.findIndex((s) => s.stepName === "PENDING_PEOPLE");
  if (peopleIndex === -1) return steps;

  return [
    ...steps.slice(0, peopleIndex + 1),
    SYNTHETIC_DOCUMENTS_STEP,
    ...steps.slice(peopleIndex + 1),
  ];
}

// Not a backend step status: the checklist sets it on steps compliance
// rejected before passing them to the step pages, so their sidebars can
// mark those steps as rejected.
export const REJECTED_STEP_STATUS = "REJECTED";

export function buildBusinessSidebar(
  steps: OnboardingStepDTO[] | undefined,
  currentStepName: string
): {
  labels: string[];
  activeIndex: number;
  completedFlags: boolean[];
  rejectedFlags: boolean[];
} {
  if (!steps || steps.length === 0) {
    return {
      labels: [],
      activeIndex: 0,
      completedFlags: [],
      rejectedFlags: [],
    };
  }

  const visible = withPersonalDetailsSelfieGate(steps).filter(
    (s) => s.stepName && !HIDDEN_STEPS.has(s.stepName)
  );
  const labels = visible.map((s) => STEP_LABEL_MAP[s.stepName!] ?? s.stepName!);
  const completedFlags = visible.map((s) => s.status === "COMPLETED");
  const rejectedFlags = visible.map((s) => s.status === REJECTED_STEP_STATUS);
  const activeIndex = Math.max(
    0,
    visible.findIndex((s) => s.stepName === currentStepName)
  );

  return { labels, activeIndex, completedFlags, rejectedFlags };
}

// Abbreviations that stay upper-case when a document type is turned into a
// label. Add to this as new document types come up.
const DOCUMENT_LABEL_ABBREVIATIONS = new Set([
  "BN",
  "BVN",
  "CAC",
  "ID",
  "NIN",
  "RC",
  "SCUML",
  "TIN",
  "VAT",
]);
const DOCUMENT_LABEL_MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "by",
  "for",
  "in",
  "of",
  "on",
  "the",
  "to",
]);

// Turns an API document type such as "ADDITIONAL_NIN" or
// "ADDITIONAL_UTILITY_BILL" into "Additional NIN" / "Additional Utility Bill".
export function formatDocumentTypeLabel(type: string): string {
  return type
    .replace(/^(ADDITIONAL_)+/, "ADDITIONAL_")
    .split("_")
    .filter(Boolean)
    .map((word, index) => {
      const upper = word.toUpperCase();
      if (DOCUMENT_LABEL_ABBREVIATIONS.has(upper)) return upper;
      const lower = word.toLowerCase();
      if (index > 0 && DOCUMENT_LABEL_MINOR_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

// A country as nationality works with it: the full name is the value saved
// to the API, and the aliases let a BVN result such as "NIGERIAN" or "NGA"
// pick the right one.
export type NationalityCountry = {
  name: string;
  iso2: string;
  aliases: string[];
};

// Shown until getAllCountries responds, or if it fails. Also supplies the
// "-ian" forms the API doesn't have, matched to its countries by ISO code.
export const FALLBACK_NATIONALITY_COUNTRIES: NationalityCountry[] = [
  { name: "Nigeria", iso2: "NG", aliases: ["Nigerian", "NGA"] },
  { name: "Ghana", iso2: "GH", aliases: ["Ghanaian", "GHA"] },
  { name: "Kenya", iso2: "KE", aliases: ["Kenyan", "KEN"] },
  { name: "South Africa", iso2: "ZA", aliases: ["South African", "ZAF"] },
  { name: "Cameroon", iso2: "CM", aliases: ["Cameroonian", "CMR"] },
  { name: "Benin", iso2: "BJ", aliases: ["Beninese", "BEN"] },
  { name: "Togo", iso2: "TG", aliases: ["Togolese", "TGO"] },
  { name: "Senegal", iso2: "SN", aliases: ["Senegalese", "SEN"] },
  {
    name: "Côte d'Ivoire",
    iso2: "CI",
    aliases: ["Ivory Coast", "Ivorian", "CIV"],
  },
  {
    name: "United Kingdom",
    iso2: "GB",
    aliases: ["British", "UK", "GBR"],
  },
  {
    name: "United States",
    iso2: "US",
    aliases: ["United States of America", "American", "USA"],
  },
  { name: "Canada", iso2: "CA", aliases: ["Canadian", "CAN"] },
  { name: "China", iso2: "CN", aliases: ["Chinese", "CHN"] },
  { name: "India", iso2: "IN", aliases: ["Indian", "IND"] },
  { name: "Lebanon", iso2: "LB", aliases: ["Lebanese", "LBN"] },
];

// Turns getAllCountries results into nationality countries, keeping the
// fallback list's extra names (e.g. "Nigerian", "UK") for the same country
export function toNationalityCountries(
  countries: { name?: string; iso2Code?: string; iso3Code?: string }[]
): NationalityCountry[] {
  return countries
    .filter((country) => country.name && country.iso2Code)
    .map((country) => {
      const iso2 = country.iso2Code!.toUpperCase();
      const known = FALLBACK_NATIONALITY_COUNTRIES.find(
        (fallback) => fallback.iso2 === iso2
      );
      return {
        name: country.name!,
        iso2,
        aliases: [
          ...(country.iso3Code ? [country.iso3Code] : []),
          ...(known ? [known.name, ...known.aliases] : []),
        ],
      };
    });
}

const normaliseCountry = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findNationalityCountry = (
  raw: string | null | undefined,
  countries: NationalityCountry[]
): NationalityCountry | undefined => {
  const key = normaliseCountry(raw ?? "");
  if (!key) return undefined;
  return countries.find(({ name, iso2, aliases }) =>
    [name, iso2, ...aliases].some((option) => normaliseCountry(option) === key)
  );
};

// Maps a nationality from the BVN check or a saved record to one of the
// countries. Anything that doesn't match is returned as it came, so it
// isn't lost; getNationalityOptions keeps it selectable.
export function matchNationality(
  raw: string | null | undefined,
  countries: NationalityCountry[] = FALLBACK_NATIONALITY_COUNTRIES
): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  return findNationalityCountry(value, countries)?.name ?? value;
}

// A non-Nigerian director identifies with a Resident Permit
export function isNonNigerianNationality(
  nationality: string,
  countries: NationalityCountry[] = FALLBACK_NATIONALITY_COUNTRIES
): boolean {
  if (!nationality.trim()) return false;
  const country =
    findNationalityCountry(nationality, countries) ??
    findNationalityCountry(nationality, FALLBACK_NATIONALITY_COUNTRIES);
  return country?.iso2 !== "NG";
}

// Select items for nationality: Nigeria first, then A–Z, plus the current
// value if it isn't one of the countries (e.g. an unmatched BVN result)
export function getNationalityOptions(
  current: string,
  countries: NationalityCountry[] = FALLBACK_NATIONALITY_COUNTRIES
): { value: string; label: string }[] {
  const options = [...countries]
    .sort((a, b) =>
      a.iso2 === "NG" ? -1 : b.iso2 === "NG" ? 1 : a.name.localeCompare(b.name)
    )
    .map(({ name }) => ({ value: name, label: name }));
  return current && !options.some((option) => option.value === current)
    ? [{ value: current, label: current }, ...options]
    : options;
}
